import { IconActionChevronDownSmall16, IconActionChevronUpSmall16 } from '@cian/ui-kit-design-tokens/icons';
import { Link } from '@cian/ui-kit/link';
import { Body2 } from '@cian/ui-kit/typography';
import { type KeyboardEvent, useLayoutEffect, useRef } from 'react';

import type {
  ICategoryOffer,
  IOffersSort,
  TOffersDealType,
  TOffersSortColumn,
  TSortDirection,
} from 'shared/types/buildingOffers';
import { formatArea } from 'shared/utils/format';
import { formatOfferFloor, formatOfferPriceCell, getNextSortDirection } from 'shared/utils/buildingOffers';

import style from './style.css';

export interface IOffersTableProps {
  dealType: TOffersDealType;
  offers: ICategoryOffer[];
  selectedIndex: number;
  sort: IOffersSort | null;
  /** «Показать все N объявлений»; null — все объявления уже в таблице */
  moreLink: { url: string; text: string } | null;
  onSort(sort: IOffersSort): void;
  onSelect(index: number): void;
  /** Середина выбранной строки от верха таблицы — для стрелки превью */
  onSelectedRowMiddle(top: number): void;
}

interface ISortHeaderProps {
  title: string;
  column: TOffersSortColumn;
  dealType: TOffersDealType;
  sort: IOffersSort | null;
  onSort(sort: IOffersSort): void;
}

const SortArrow = ({ direction, isActive }: { direction: TSortDirection; isActive: boolean }) => {
  const color = isActive ? 'icon-primary-default' : 'icon-secondary-default';

  return direction === 'desc' ? (
    <IconActionChevronDownSmall16 color={color} />
  ) : (
    <IconActionChevronUpSmall16 color={color} />
  );
};

/**
 * Заголовок сортируемой колонки (`table_list.tsx` монолита): у активной стрелка — текущее направление,
 * у неактивной — направление следующего клика. Активная «только по возрастанию» не кликабельна
 */
const SortHeader = ({ title, column, dealType, sort, onSort }: ISortHeaderProps) => {
  const isActive = sort?.column === column;
  const next = getNextSortDirection(dealType, column, sort);
  const arrow = isActive ? sort.direction : next;

  return (
    <button
      type="button"
      className={`${style['sort']} ${isActive ? style['sort--active'] : ''}`}
      disabled={next === null}
      data-name="OffersSort"
      onClick={() => next && onSort({ column, direction: next })}
    >
      {title}
      {arrow && <SortArrow direction={arrow} isActive={isActive} />}
    </button>
  );
};

/** Таблица объявлений: колонки зависят только от сделки (`serp_offers_list.tsx` монолита) */
export const OffersTable = ({
  dealType,
  offers,
  selectedIndex,
  sort,
  moreLink,
  onSort,
  onSelect,
  onSelectedRowMiddle,
}: IOffersTableProps) => {
  const rowsRef = useRef<Array<HTMLTableRowElement | null>>([]);

  useLayoutEffect(() => {
    const row = rowsRef.current[selectedIndex];

    if (row) {
      onSelectedRowMiddle(row.offsetTop + row.offsetHeight / 2);
    }
  }, [selectedIndex, offers, onSelectedRowMiddle]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, index: number) => {
    if (event.key === 'Enter') {
      onSelect(index);
    }
  };

  return (
    <div className={style['container']}>
      <table className={style['table']} data-name="OffersTable">
        <thead>
          <tr>
            <th className={style['head']}>
              <Body2 color="text-secondary-default">Тип сделки</Body2>
            </th>
            <th className={style['head']}>
              <Body2 color="text-secondary-default">Этаж</Body2>
            </th>
            <th className={style['head']}>
              <SortHeader title="Площадь" column="area" dealType={dealType} sort={sort} onSort={onSort} />
            </th>
            <th className={`${style['head']} ${style['price']}`}>
              <SortHeader
                title={dealType === 'rent' ? 'Ставка за м² в год' : 'Цена'}
                column="price"
                dealType={dealType}
                sort={sort}
                onSort={onSort}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {offers.map((offer, index) => (
            <tr
              key={offer.cianId}
              ref={node => {
                rowsRef.current[index] = node;
              }}
              className={`${style['row']} ${index === selectedIndex ? style['row--selected'] : ''}`}
              tabIndex={0}
              aria-selected={index === selectedIndex}
              data-name="OffersTableRow"
              onClick={() => onSelect(index)}
              onKeyDown={event => handleKeyDown(event, index)}
            >
              <td className={style['cell']}>
                <Body2>{dealType === 'rent' ? 'аренда' : 'продажа'}</Body2>
              </td>
              <td className={style['cell']}>
                <Body2>{formatOfferFloor(offer)}</Body2>
              </td>
              <td className={style['cell']}>
                <Body2>{offer.totalArea === null ? '' : formatArea(offer.totalArea)}</Body2>
              </td>
              <td className={`${style['cell']} ${style['price']}`}>
                <Body2>{formatOfferPriceCell(offer, dealType)}</Body2>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {moreLink && (
        <div className={style['more']}>
          <Link href={moreLink.url} target="_blank" fontSize="S" data-name="OffersMoreLink">
            {moreLink.text}
          </Link>
        </div>
      )}
    </div>
  );
};
